import type { Role } from '../domain/models';

export type NavigationRole = Extract<Role, 'child' | 'parent' | 'teacher' | 'institution' | 'admin'>;
export type NavigationIcon =
  | 'home' | 'record' | 'mission' | 'concept' | 'badge' | 'profile' | 'village' | 'collection'
  | 'approval' | 'reward' | 'report' | 'talk'
  | 'users' | 'content' | 'asset' | 'analytics' | 'audit' | 'settings' | 'permissions' | 'notice' | 'module' | 'school' | 'assignment' | 'team';

export interface NavigationItem {
  id: string;
  label: string;
  shortLabel?: string;
  to: string;
  icon: NavigationIcon;
  featured?: boolean;
  group?: string;
  aliases?: string[];
  badgeKey?: 'pendingMissions' | 'notices';
}

export const childNavigation: NavigationItem[] = [
  { id:'child-home', label:'홈', to:'/child/home', icon:'home', aliases:['/07','/child/personalization','/child/personalization/report'] },
  // 경제도서관 (/child/concepts) is a place you enter from the village hub,
  // not a step in the daily article flow. It sat under 탐험, so tapping a
  // 마을 card moved the lit tab to 탐험 and the bar contradicted the screen.
  { id:'child-explore', label:'탐험', to:'/today/article', icon:'concept', aliases:['/today/quiz','/today/mission','/today/reward','/child/concepts/economy'] },
  { id:'child-village', label:'마을', to:'/village/hub', icon:'village', aliases:['/village','/child/growth-board','/child/fridge','/child/weekly-report','/child/first-week','/child/weekly-expedition','/child/family-expedition','/child/concepts','/child/library'] },
  { id:'child-collection', label:'컬렉션', to:'/collection/puzzle', icon:'collection', aliases:['/child/badges','/child/hall-of-honor','/child/certificates','/18'] }
];

export const parentNavigation: NavigationItem[] = [
  { id: 'parent-check', label: '확인', to: '/parent/home', icon: 'approval', aliases: ['/10','/11','/parent/approvals','/parent/passport'], badgeKey: 'pendingMissions' },
  { id: 'parent-growth', label: '성장', to: '/parent/weekly-insight', icon: 'report', aliases: ['/13','/25','/parent/reports','/parent/records','/parent/reports/personalization'] },
  { id: 'parent-family', label: '가족', to: '/parent/family-expedition', icon: 'talk', aliases: ['/12','/15','/26','/parent/family-missions','/parent/rewards','/parent/family-talk'] },
  { id: 'parent-settings', label: '설정', to: '/security/pin', icon: 'settings', aliases: ['/consent'] }
];

export const adminNavigation: NavigationItem[] = [
  { id: 'admin-dashboard', label: '대시보드', to: '/admin', icon: 'home', group: '운영' },
  { id: 'admin-users', label: '사용자·가족', to: '/admin/users', icon: 'users', group: '운영' },
  { id: 'admin-inquiries', label: '문의·신고', to: '/admin/inquiries', icon: 'talk', group: '운영' },
  { id: 'admin-missions', label: '미션', to: '/admin/content/missions', icon: 'mission', group: '콘텐츠' },
  { id: 'admin-badges', label: '배지', to: '/admin/content/badges', icon: 'badge', group: '콘텐츠' },
  { id: 'admin-notices', label: '공지·배너', to: '/admin/content/notices', icon: 'notice', group: '콘텐츠' },
  { id: 'admin-economy', label: '경제기사·퀴즈', to: '/admin/content/economy', icon: 'content', group: '콘텐츠' },
  { id: 'admin-content-studio', label: '콘텐츠 스튜디오', to: '/admin/content/studio', icon: 'content', group: '콘텐츠' },
  { id: 'admin-personalization', label: '개인화 추천', to: '/admin/content/personalization', icon: 'analytics', group: '콘텐츠' },
  { id: 'admin-library', label: '경제도서관', to: '/admin/content/library', icon: 'concept', group: '콘텐츠' },
  { id: 'admin-reward-policies', label: '가족 미션·용돈', to: '/admin/content/reward-policies', icon: 'reward', group: '콘텐츠' },
  { id: 'admin-passport', label: '금융 패스포트', to: '/admin/content/passport', icon: 'record', group: '콘텐츠' },
  { id: 'admin-school', label: '학교·교사 운영', to: '/admin/school', icon: 'school', group: '운영' },
  { id: 'admin-cooperative', label: '협동 미션·보물찾기', to: '/admin/content/cooperative', icon: 'team', group: '콘텐츠' },
  { id: 'admin-collections', label: '성장보드·냥장고', to: '/admin/content/collections', icon: 'badge', group: '콘텐츠' },
  { id: 'admin-assets', label: '캐릭터 에셋', to: '/admin/assets', icon: 'asset', group: '디자인 자산' },
  { id: 'admin-modules', label: '모듈 레지스트리', to: '/admin/modules', icon: 'module', group: '디자인 자산' },
  { id: 'admin-reports', label: '통계·리포트', to: '/admin/reports', icon: 'analytics', group: '분석' },
  { id: 'admin-audit', label: '감사 로그', to: '/admin/audit', icon: 'audit', group: '분석' },
  { id: 'admin-permissions', label: '권한 관리', to: '/admin/permissions', icon: 'permissions', group: '시스템' },
  { id: 'admin-settings', label: '시스템 설정', to: '/admin/settings', icon: 'settings', group: '시스템' }
];



export const teacherNavigation: NavigationItem[] = [
  { id:'teacher-home', label:'홈', to:'/teacher', icon:'home' },
  { id:'teacher-classrooms', label:'학급', to:'/teacher/classrooms', icon:'school' },
  { id:'teacher-assignments', label:'과제', to:'/teacher/assignments', icon:'assignment' },
  { id:'teacher-cooperative', label:'협동', to:'/teacher/cooperative', icon:'team' },
  { id:'teacher-reports', label:'리포트', to:'/teacher/reports', icon:'report' },
  { id:'teacher-settings', label:'설정', to:'/teacher/settings', icon:'settings' }
];

export const institutionNavigation: NavigationItem[] = [
  { id:'institution-home', label:'대시보드', shortLabel:'홈', to:'/institution', icon:'home' },
  { id:'institution-schools', label:'학교·교사', shortLabel:'조직', to:'/institution/schools', icon:'school' },
  { id:'institution-content', label:'콘텐츠 배정', shortLabel:'배정', to:'/institution/content', icon:'assignment' },
  { id:'institution-reports', label:'통계', to:'/institution/reports', icon:'analytics' },
  { id:'institution-audit', label:'감사', to:'/institution/audit', icon:'audit' }
];

export function navigationFor(role: NavigationRole): NavigationItem[] {
  if (role === 'child') return childNavigation;
  if (role === 'parent') return parentNavigation;
  if (role === 'teacher') return teacherNavigation;
  if (role === 'institution') return institutionNavigation;
  return adminNavigation;
}

export function navigationItemActive(item: NavigationItem, pathname: string): boolean {
  return navigationMatchLength(item, pathname) >= 0;
}

/** How specifically this item claims the path, or -1 if it does not. */
function navigationMatchLength(item: NavigationItem, pathname: string): number {
  let best = -1;
  for (const candidate of [item.to, ...(item.aliases ?? [])]) {
    if (pathname === candidate || pathname.startsWith(`${candidate}/`)) best = Math.max(best, candidate.length);
  }
  return best;
}

/**
 * The one item that owns this path.
 *
 * Two items can both claim a path — /child/concepts belongs to 마을 while
 * /child/concepts/economy belongs to 탐험 — and lighting both tabs tells the
 * visitor nothing. The longer, more specific claim wins; ties go to the
 * earlier item so the order on screen stays the tie-breaker.
 */
export function activeNavigationId(items: NavigationItem[], pathname: string): string | null {
  let winner: string | null = null;
  let best = -1;
  for (const item of items) {
    const length = navigationMatchLength(item, pathname);
    if (length > best) { best = length; winner = item.id; }
  }
  return best >= 0 ? winner : null;
}
